import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  useTheme
} from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const PortfolioPerformance = ({ trades = [] }) => {
  const [timeframe, setTimeframe] = useState('1M');
  const [portfolioData, setPortfolioData] = useState([]);
  const [equityCurve, setEquityCurve] = useState([]);
  const [drawdownData, setDrawdownData] = useState([]);
  const theme = useTheme();

  useEffect(() => {
    if (trades.length > 0) {
      calculatePortfolioMetrics(trades, timeframe);
    }
  }, [trades, timeframe]);

  const calculatePortfolioMetrics = (trades, period) => {
    // Sort trades by date
    const sortedTrades = [...trades].sort((a, b) => new Date(a.tradeDate) - new Date(b.tradeDate));
    
    // Filter trades based on selected timeframe
    const now = new Date();
    const filteredTrades = sortedTrades.filter(trade => {
      const tradeDate = new Date(trade.tradeDate);
      switch(period) {
        case '1W': return (now - tradeDate) <= 7 * 24 * 60 * 60 * 1000;
        case '1M': return (now - tradeDate) <= 30 * 24 * 60 * 60 * 1000;
        case '3M': return (now - tradeDate) <= 90 * 24 * 60 * 60 * 1000;
        case '6M': return (now - tradeDate) <= 180 * 24 * 60 * 60 * 1000;
        case '1Y': return (now - tradeDate) <= 365 * 24 * 60 * 60 * 1000;
        case 'ALL': return true;
        default: return true;
      }
    });

    // Calculate equity curve
    let balance = 10000; // Starting balance
    let peak = balance;
    const equity = [];
    const drawdowns = [];

    filteredTrades.forEach((trade, index) => {
      const pnl = parseFloat(trade.profitLoss || 0);
      balance += pnl;
      
      // Update peak if we have a new high
      if (balance > peak) {
        peak = balance;
      }
      
      // Calculate drawdown
      const drawdown = ((peak - balance) / peak) * 100;
      
      equity.push({
        date: new Date(trade.tradeDate).toLocaleDateString(),
        balance,
        trade: trade.symbol,
        pnl
      });
      
      drawdowns.push({
        date: new Date(trade.tradeDate).toLocaleDateString(),
        drawdown: drawdown.toFixed(2)
      });
    });

    setEquityCurve(equity);
    setDrawdownData(drawdowns);
    
    // Calculate portfolio metrics
    const totalTrades = filteredTrades.length;
    const winningTrades = filteredTrades.filter(t => t.status === 'WIN').length;
    const losingTrades = filteredTrades.filter(t => t.status === 'LOSS').length;
    const totalPnL = filteredTrades.reduce((sum, t) => sum + parseFloat(t.profitLoss || 0), 0);
    const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;
    
    // Calculate profit factor
    const grossProfit = filteredTrades
      .filter(t => parseFloat(t.profitLoss || 0) > 0)
      .reduce((sum, t) => sum + parseFloat(t.profitLoss || 0), 0);
      
    const grossLoss = Math.abs(filteredTrades
      .filter(t => parseFloat(t.profitLoss || 0) < 0)
      .reduce((sum, t) => sum + parseFloat(t.profitLoss || 0), 0));
    
    const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0;
    
    // Calculate max drawdown
    const maxDrawdown = drawdowns.length > 0 ? 
      Math.max(...drawdowns.map(d => parseFloat(d.drawdown))) : 0;
    
    // Calculate Sharpe ratio (simplified)
    const returns = filteredTrades.map(t => parseFloat(t.profitLoss || 0) / 10000); // Assuming $10k account
    const avgReturn = returns.reduce((sum, r) => sum + r, 0) / returns.length;
    const stdDev = Math.sqrt(
      returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / returns.length
    );
    const sharpeRatio = stdDev > 0 ? avgReturn / stdDev : 0;
    
    // Calculate average trade
    const avgTrade = totalTrades > 0 ? totalPnL / totalTrades : 0;
    
    // Calculate average win and loss
    const avgWin = winningTrades > 0 ? 
      filteredTrades
        .filter(t => t.status === 'WIN')
        .reduce((sum, t) => sum + parseFloat(t.profitLoss || 0), 0) / winningTrades : 0;
        
    const avgLoss = losingTrades > 0 ? 
      filteredTrades
        .filter(t => t.status === 'LOSS')
        .reduce((sum, t) => sum + parseFloat(t.profitLoss || 0), 0) / losingTrades : 0;
    
    // Calculate expectancy
    const expectancy = (winRate / 100 * avgWin) + ((100 - winRate) / 100 * avgLoss);
    
    setPortfolioData([
      { name: 'Win Rate', value: winRate.toFixed(2) + '%' },
      { name: 'Profit Factor', value: profitFactor.toFixed(2) },
      { name: 'Max Drawdown', value: maxDrawdown.toFixed(2) + '%' },
      { name: 'Sharpe Ratio', value: sharpeRatio.toFixed(2) },
      { name: 'Avg Trade', value: '$' + avgTrade.toFixed(2) },
      { name: 'Expectancy', value: '$' + expectancy.toFixed(2) },
      { name: 'Avg Win', value: '$' + avgWin.toFixed(2) },
      { name: 'Avg Loss', value: '$' + avgLoss.toFixed(2) },
    ]);
  };

  const handleTimeframeChange = (event, newValue) => {
    setTimeframe(newValue);
  };

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
  ];

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
          Portfolio Performance
        </Typography>
        
        <Tabs
          value={timeframe}
          onChange={handleTimeframeChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{ mb: 3 }}
        >
          <Tab label="1W" value="1W" />
          <Tab label="1M" value="1M" />
          <Tab label="3M" value="3M" />
          <Tab label="6M" value="6M" />
          <Tab label="1Y" value="1Y" />
          <Tab label="All" value="ALL" />
        </Tabs>
        
        <Grid container spacing={3}>
          {/* Key Metrics */}
          <Grid item xs={12} md={4}>
            <Card elevation={1} sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Key Metrics
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  {portfolioData.map((item, index) => (
                    <Grid item xs={6} key={index}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {item.name}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {item.value}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Equity Curve */}
          <Grid item xs={12} md={8}>
            <Card elevation={1} sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Equity Curve
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={equityCurve}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value, name) => [`$${value.toFixed(2)}`, name]}
                        labelFormatter={(label) => `Date: ${label}`}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="balance" 
                        name="Account Balance" 
                        stroke={theme.palette.primary.main} 
                        fill={theme.palette.primary.light} 
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Drawdown Chart */}
          <Grid item xs={12} md={6}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Drawdown
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ height: 250 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={drawdownData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value) => [`${value}%`, 'Drawdown']}
                        labelFormatter={(label) => `Date: ${label}`}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="drawdown" 
                        name="Drawdown" 
                        stroke={theme.palette.error.main} 
                        fill={theme.palette.error.light} 
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Win/Loss Distribution */}
          <Grid item xs={12} md={6}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Win/Loss Distribution
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ height: 250, display: 'flex', justifyContent: 'center' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Wins', value: trades.filter(t => t.status === 'WIN').length },
                          { name: 'Losses', value: trades.filter(t => t.status === 'LOSS').length },
                          { name: 'Breakeven', value: trades.filter(t => t.status === 'BREAKEVEN').length },
                          { name: 'Open', value: trades.filter(t => t.status === 'OPEN').length }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {[
                          { name: 'Wins', color: theme.palette.success.main },
                          { name: 'Losses', color: theme.palette.error.main },
                          { name: 'Breakeven', color: theme.palette.warning.main },
                          { name: 'Open', color: theme.palette.info.main }
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [value, 'Trades']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default PortfolioPerformance;